import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, MapPin, Clock, Eye, Calendar, Tag, AlertCircle } from 'lucide-react';
import { useGetAdBySlugQuery, useAppSelector, selectCurrentUser } from '@nimbazaar/store';
import { ImageGallery } from '../components/ImageGallery';
import { SellerProfileCard } from '../components/SellerProfileCard';
import { BidSubmissionForm } from '../components/BidSubmissionForm';
import { BidSection } from '../components/BidSection';
import { CommentSection } from '../components/CommentSection';
import { RelatedAds } from '../components/RelatedAds';
import { AdOwnerControls } from '../components/AdOwnerControls';
import { AuthModal } from '../components/AuthModal';

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function formatExpiryTime(date: Date | string): string {
  const now = new Date();
  const expiry = new Date(date);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    return 'Expired';
  } else if (diffDays === 0) {
    return 'Expires today';
  } else if (diffDays === 1) {
    return 'Expires tomorrow';
  } else {
    return `Expires in ${diffDays}d`;
  }
}

function formatBudget(min?: number, max?: number): string {
  const format = (n: number) => {
    if (n >= 10000000) {
      return `Rs. ${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Crore`;
    } else if (n >= 100000) {
      return `Rs. ${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} Lac`;
    } else if (n >= 1000) {
      return `Rs. ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)} Thousand`;
    }
    return `Rs. ${n.toLocaleString()}`;
  };

  if (min && max) {
    return `${format(min)} - ${format(max)}`;
  } else if (min) {
    return `From ${format(min)}`;
  } else if (max) {
    return `Up to ${format(max)}`;
  }
  return 'Budget not specified';
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb skeleton */}
        <div className="h-4 bg-gray-200 rounded w-48 mb-6" />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main content skeleton */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image skeleton */}
            <div className="bg-gray-200 rounded-xl h-64 md:h-80 lg:h-96" />

            {/* Title skeleton */}
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-6 bg-gray-200 rounded w-32" />
            </div>

            {/* Description skeleton */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          </div>

          {/* Sidebar skeleton */}
          <div className="lg:col-span-1">
            <div className="bg-gray-200 rounded-xl h-80" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-600 mb-6">{message}</p>
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="text-center">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-12 h-12 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Ad Not Found</h2>
        <p className="text-gray-600 mb-6">
          The ad you're looking for doesn't exist or has been removed.
        </p>
        <Link
          to="/"
          className="inline-flex px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
        >
          Go to Homepage
        </Link>
      </div>
    </div>
  );
}

export function AdDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError, error, refetch } = useGetAdBySlugQuery(slug || '', {
    skip: !slug,
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const currentUser = useAppSelector(selectCurrentUser);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError) {
    const errorData = error as { status?: number; data?: { error?: { message?: string } } };
    if (errorData?.status === 404) {
      return <NotFoundState />;
    }
    return (
      <ErrorState
        message={errorData?.data?.error?.message || 'Failed to load ad details'}
        onRetry={refetch}
      />
    );
  }

  if (!data?.data) {
    return <NotFoundState />;
  }

  const ad = data.data;

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm mb-4 sm:mb-6 overflow-hidden">
          <Link to="/" className="text-gray-500 hover:text-primary-500 transition-colors shrink-0">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 shrink-0" />
          <Link
            to={`/category/${ad.category.slug}`}
            className="text-gray-500 hover:text-primary-500 transition-colors shrink-0"
          >
            {ad.category.name}
          </Link>
          <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 shrink-0" />
          <span className="text-gray-900 font-medium truncate min-w-0">
            {ad.title}
          </span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <ImageGallery images={ad.images} title={ad.title} />

            {/* Ad Info Card */}
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
              {/* Title */}
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
                {ad.title}
              </h1>

              {/* Budget */}
              <div className="text-secondary-500 font-bold text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4">
                {formatBudget(ad.minBudget, ad.maxBudget)}
              </div>

              {/* Category & Location Tags */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <span className="inline-flex items-center gap-1.5 bg-secondary-50 text-secondary-600 text-sm font-medium px-3 py-1.5 rounded-full">
                  <Tag className="w-4 h-4" />
                  {ad.category.name}
                </span>
                {ad.location && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-sm font-medium px-3 py-1.5 rounded-full">
                    <MapPin className="w-4 h-4" />
                    {ad.location}
                  </span>
                )}
              </div>

              {/* Description */}
              <div className="border-t border-gray-100 pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
                <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                  {ad.description}
                </p>
              </div>

              {/* Stats */}
              <div className="border-t border-gray-100 mt-6 pt-6">
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>Posted {formatRelativeTime(ad.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Eye className="w-4 h-4" />
                    <span>{ad.viewCount.toLocaleString()} views</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>{formatExpiryTime(ad.expiresAt)}</span>
                  </div>
                </div>
              </div>

              {/* Owner Controls */}
              {ad.isOwner && (
                <AdOwnerControls
                  adId={ad.id}
                  adSlug={slug || ''}
                  status={ad.status}
                  bidsVisible={ad.bidsVisible}
                />
              )}
            </div>

            {/* Mobile Seller Card - shows below content on mobile */}
            <div className="lg:hidden">
              <SellerProfileCard seller={ad.user} adTitle={ad.title} adId={ad.id} />
            </div>

            {/* Bid Submission Form */}
            <BidSubmissionForm
              adId={ad.id}
              adSlug={slug || ''}
              isOwner={ad.isOwner}
              existingBidUserId={
                currentUser
                  ? ad.bids?.find((b) => b.user.id === currentUser.id)?.user.id
                  : undefined
              }
              onLoginClick={() => setShowAuthModal(true)}
            />

            {/* Bids Section */}
            <BidSection
              bids={ad.bids || []}
              bidCount={ad.bidCount}
              bidsVisible={ad.bidsVisible}
              isOwner={ad.isOwner}
              currentUserId={currentUser?.id}
            />

            {/* Comments Section */}
            <CommentSection
              adId={ad.id}
              onLoginClick={() => setShowAuthModal(true)}
            />
          </div>

          {/* Sidebar - Desktop */}
          <div className="hidden lg:block lg:col-span-1">
            <SellerProfileCard seller={ad.user} adTitle={ad.title} adId={ad.id} />
          </div>
        </div>
      </div>

      {/* Related Ads Section */}
      <RelatedAds
        adId={ad.id}
        categoryName={ad.category.name}
        categorySlug={ad.category.slug}
      />

      {/* Auth Modal for Login/Signup */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
